import { useId } from "react";
import { Trash2, Plus, AlertTriangle } from "@/components/icons";
import type { FeeType, Uom } from "@/lib/api";
import {
  type PricingFormValues, type PricingRuleValues, UOM_BY_FEE, newRule, formPricingMode, savedRateTooPrecise,
} from "@/lib/pricingForm";
import { currencyStatus, savedRateText, type TenantCurrency } from "@/lib/moneyManagement";
import { useT } from "@/app/localization";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// Index = the stored day number (Sun=0 .. Sat=6); the code selects the label.
const DAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

function daysToSet(csv?: string): Set<number> {
  if (!csv) return new Set();
  return new Set(csv.split(",").map((x) => parseInt(x.trim(), 10)).filter((n) => !Number.isNaN(n)));
}
function setToDays(s: Set<number>): string {
  return [...s].sort((a, b) => a - b).join(",");
}

/**
 * The ONE shared pricing-rule editor. Controlled (value + onChange). Used identically by
 * the charger default-tariff editor and the Pricing Profiles create/edit form — same
 * fields (read-only currency; per-rule fee type charging/idle/activation, unit, rate,
 * optional time-of-use window with days + priority), same validation/serialization
 * (lib/pricingForm). The parent owns saving; this component renders nothing about WHERE
 * it is saved.
 *
 * The currency is not editable: it is the saved tariff's own, or the tenant's operating
 * currency for new prices. When the server does not allow saving prices in it for this
 * tenant, the form says why (the parent keeps saving disabled). Rates are kept as the
 * exact text typed or sent by the server.
 */
export function PricingForm({
  value, onChange, tenant, idPrefix = "pricing",
}: {
  value: PricingFormValues;
  onChange: (v: PricingFormValues) => void;
  tenant: TenantCurrency;
  idPrefix?: string;
}) {
  const t = useT();
  const id = useId();
  const { rules, currency } = value;
  const setRules = (rules: PricingRuleValues[]) => onChange({ ...value, rules });

  const updateRule = (i: number, patch: Partial<PricingRuleValues>) =>
    setRules(rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const changeFeeType = (i: number, fee: FeeType) =>
    updateRule(i, { fee_type: fee, uom: UOM_BY_FEE[fee][0] });
  const addRule = () => setRules([...rules, newRule()]);
  const removeRule = (i: number) => setRules(rules.filter((_, idx) => idx !== i));
  const toggleDay = (i: number, day: number) => {
    const s = daysToSet(rules[i].days);
    if (s.has(day)) s.delete(day); else s.add(day);
    updateRule(i, { days: setToDays(s) });
  };
  const applyFreeWindowPreset = () =>
    setRules([
      { fee_type: "charging", uom: "kwh", rate: "0.3", is_tou: false, start_time: "", end_time: "", days: "", priority: 0 },
      { fee_type: "charging", uom: "kwh", rate: "0", is_tou: true, start_time: "09:00", end_time: "18:00", days: "", priority: 10 },
    ]);

  const mode = formPricingMode(value);
  const currencyId = `${id}-currency`;
  const status = currencyStatus(value, tenant);

  return (
    <div className="flex flex-col gap-3" data-testid="pricing-form">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <Field className="w-28 gap-1">
          <FieldLabel className="text-xs" htmlFor={currencyId}>{t("billing.tariffs.currency_label")}</FieldLabel>
          <Input id={currencyId} data-testid="pricing-currency" value={currency || t("common.state.unavailable")} readOnly />
        </Field>
        <div className="flex items-center gap-2">
          {mode !== null && (
            <Badge variant={mode === "free" ? "success" : "neutral"} className="h-6 px-2.5 text-sm">
              {t("billing.tariffs.mode_badge", { mode })}
            </Badge>
          )}
          <Button type="button" variant="ghost" size="sm" onClick={applyFreeWindowPreset}>
            {t("billing.tariffs.preset_free_window")}
          </Button>
        </div>
      </div>

      {status !== "writable" && (
        <Alert data-testid="pricing-currency-blocked">
          <AlertTriangle />
          <AlertDescription>
            {status === "other_currency"
              ? t("billing.tariffs.currency_blocked", { currency })
              : status === "not_ready"
                ? t("billing.tariffs.currency_not_ready", { currency })
                : t("billing.tariffs.currency_unavailable")}
          </AlertDescription>
        </Alert>
      )}

      {rules.length === 0 && (
        <div className="text-sm opacity-60" data-testid="pricing-empty">
          {t("billing.tariffs.empty")}
        </div>
      )}

      <div className="flex flex-col gap-3">
        {rules.map((rule, i) => {
          const uoms = UOM_BY_FEE[rule.fee_type] ?? [rule.uom];
          const daySet = daysToSet(rule.days);
          // A saved rule whose rate or unit cannot be read keeps its stored text and is read-only.
          const locked = rule.saved?.kind === "unreadable";
          const tooPrecise = savedRateTooPrecise(rule);
          const ruleId = `${id}-rule-${i}`;
          return (
            <div key={i} className="flex flex-col gap-3 rounded-md border p-3" data-testid={`pricing-rule-${i}`}>
              <div className="flex flex-wrap items-end gap-3">
                <Field className="w-32 gap-1" data-disabled={locked || undefined}>
                  <FieldLabel className="text-xs" htmlFor={`${ruleId}-fee`}>{t("billing.tariffs.fee_type_label")}</FieldLabel>
                  <Select value={rule.fee_type} onValueChange={(v) => changeFeeType(i, v as FeeType)} disabled={locked}>
                    <SelectTrigger id={`${ruleId}-fee`} className="w-full" data-testid={`pricing-feetype-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="charging">{t("billing.tariffs.fee_type", { fee: "charging" })}</SelectItem>
                        <SelectItem value="idle">{t("billing.tariffs.fee_type", { fee: "idle" })}</SelectItem>
                        <SelectItem value="activation">{t("billing.tariffs.fee_type", { fee: "activation" })}</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </Field>
                <Field className="w-28 gap-1" data-disabled={locked || uoms.length === 1 || undefined}>
                  <FieldLabel className="text-xs" htmlFor={`${ruleId}-unit`}>{t("billing.tariffs.unit_label")}</FieldLabel>
                  <Select value={rule.uom} onValueChange={(v) => updateRule(i, { uom: v as Uom })}
                    disabled={locked || uoms.length === 1}>
                    <SelectTrigger id={`${ruleId}-unit`} className="w-full"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {uoms.map((u) => <SelectItem key={u} value={u}>{t("billing.tariffs.uom", { uom: u })}</SelectItem>)}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </Field>
                <Field className="w-28 gap-1" data-invalid={locked || tooPrecise || undefined}>
                  <FieldLabel className="text-xs" htmlFor={`${ruleId}-rate`}>
                    {currency
                      ? t("billing.tariffs.rate_label", { uom: rule.uom, currency })
                      : t("billing.tariffs.rate_label_plain", { uom: rule.uom })}
                  </FieldLabel>
                  {locked ? (
                    <Input id={`${ruleId}-rate`} data-testid={`pricing-rate-${i}`} value={rule.rate} readOnly aria-invalid />
                  ) : (
                    <Input
                      id={`${ruleId}-rate`}
                      className={rule.rate === "0" || rule.rate === "" ? "placeholder:text-emerald-600 placeholder:font-medium" : undefined}
                      data-testid={`pricing-rate-${i}`}
                      type="number" step="any" min="0" placeholder={t("billing.tariffs.rate_free_placeholder")}
                      aria-invalid={tooPrecise || undefined}
                      value={rule.rate === "0" ? "" : rule.rate}
                      onChange={(e) => updateRule(i, { rate: e.target.value })} />
                  )}
                </Field>
                <div className="flex items-center gap-2 pb-2">
                  <Switch checked={!!rule.is_tou} onCheckedChange={(c) => updateRule(i, { is_tou: c })} disabled={locked} />
                  <Label className="text-xs">{t("billing.tariffs.time_window")}</Label>
                </div>
                <Button type="button" variant="ghost" size="icon" className="ml-auto" onClick={() => removeRule(i)}
                  aria-label={t("billing.tariffs.remove_rule")}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              {locked && rule.saved?.kind === "unreadable" && (
                <FieldError>{t("billing.tariffs.rule_unreadable", { raw: rule.saved.raw })}</FieldError>
              )}
              {tooPrecise && (
                <FieldError>{t("billing.tariffs.rate_cannot_save", { amount: savedRateText(rule) ?? rule.rate })}</FieldError>
              )}

              {rule.is_tou && (
                <div className="flex flex-wrap items-end gap-3 border-t pt-3">
                  <div>
                    <Label className="text-xs">{t("billing.tariffs.window_from")}</Label>
                    <Input className="mt-1 w-36 min-w-36" type="time" value={rule.start_time || ""}
                      onChange={(e) => updateRule(i, { start_time: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">{t("billing.tariffs.window_to")}</Label>
                    <Input className="mt-1 w-36 min-w-36" type="time" value={rule.end_time || ""}
                      onChange={(e) => updateRule(i, { end_time: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">{t("billing.tariffs.days_label")}</Label>
                    <div className="mt-1 flex gap-1">
                      {DAYS.map((day, d) => (
                        <button key={d} type="button" onClick={() => toggleDay(i, d)}
                          aria-label={t("billing.tariffs.day_name", { day })}
                          className={`h-7 w-7 rounded text-xs border ${daySet.has(d) ? "bg-primary text-primary-foreground" : "bg-transparent opacity-70"}`}>
                          {t("billing.tariffs.day_short", { day })}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">{t("billing.tariffs.priority_label")}</Label>
                    <Input className="mt-1 w-20" type="number" step="1" value={rule.priority ?? 0}
                      onChange={(e) => updateRule(i, { priority: parseInt(e.target.value, 10) || 0 })} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Button type="button" variant="outline" size="sm" onClick={addRule} data-testid="pricing-add-rule">
        <Plus className="h-4 w-4 mr-1" /> {t("billing.tariffs.add_rule")}
      </Button>
      <p className="text-xs opacity-50">
        {t("billing.tariffs.rules_help")}
      </p>
    </div>
  );
}
